import React, { useState } from "react";
import { Alert, Box, Link } from "@mui/material";
import Button from "components/basic/button";
import GoogleRating from "components/basic/googleRating";
import Typography from "components/basic/typography";
import { GoogleLinkReview } from "generated/graphql";
import { useSnack } from "hooks/useSnack";

import { useAdminActions } from "../../../../../../adminApi";
import { AdminVendor } from "../../../../../../types";

interface GoogleReviewPanelProps {
  vendor: AdminVendor;
  onChanged: () => void;
}

/**
 * The linked Google listing next to the business name, so an admin can tell
 * whether it's really theirs. Hidden when the vendor has no link.
 */
const GoogleReviewPanel: React.FC<GoogleReviewPanelProps> = ({
  vendor,
  onChanged,
}) => {
  const showSnack = useSnack();
  const { reviewGoogleLink } = useAdminActions();
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);

  if (!vendor.vendor.googlePlaceId) {
    return null;
  }

  const status = vendor.googleLinkStatus;
  const listing = vendor.reviewListing;

  const decide = async (approve: boolean) => {
    setBusy(approve ? "approve" : "reject");
    const result = await reviewGoogleLink(vendor.id, approve);
    setBusy(null);
    if (!result.ok) {
      showSnack(result.error ?? "Failed to review link", "error");
      return;
    }
    showSnack(approve ? "Link approved" : "Link rejected", "success");
    onChanged();
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="h5" style={{ fontWeight: 600 }}>
        Google reviews link
      </Typography>

      {status === GoogleLinkReview.Approved ? (
        <Alert severity="success">
          Approved. Its rating shows on this vendor's profile and quotes.
        </Alert>
      ) : (
        <Alert
          severity={status === GoogleLinkReview.Rejected ? "error" : "warning"}
        >
          {status === GoogleLinkReview.Rejected
            ? "Rejected. Stars stay hidden until the vendor links a different listing."
            : "The listing's name doesn't match this business. Stars stay hidden until you approve it."}
        </Alert>
      )}

      <Typography variant="body2">
        Business: <strong>{vendor.vendor.businessName}</strong>
        {vendor.vendor.operatingAs &&
          ` (operating as ${vendor.vendor.operatingAs})`}
      </Typography>
      {listing && (
        <Typography variant="body2">
          Google listing: <strong>{listing.placeName ?? "Unnamed"}</strong>{" "}
          <Link
            href={listing.reviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Maps
          </Link>
        </Typography>
      )}
      {listing && <GoogleRating listing={listing} />}
      {vendor.vendor.brandGoogleReview && (
        <Typography variant="caption" style={{ wordBreak: "break-all" }}>
          Pasted link: {vendor.vendor.brandGoogleReview}
        </Typography>
      )}

      {status !== GoogleLinkReview.Approved && (
        <Box display="flex" gap={1} mt={1}>
          <Button
            variant="contained"
            size="small"
            loading={busy === "approve"}
            disabled={busy !== null}
            onClick={() => decide(true)}
          >
            Approve
          </Button>
          {status !== GoogleLinkReview.Rejected && (
            <Button
              size="small"
              color="error"
              loading={busy === "reject"}
              disabled={busy !== null}
              onClick={() => decide(false)}
            >
              Reject
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GoogleReviewPanel;
